/**
 * OptionsPortfolioHeader — the beginner-friendly "am I OK?" roll-up for all open
 * option baskets. One glance answers: live P&L, how much is actually at risk,
 * how many positions, the nearest expiry, and whether anything needs attention.
 * Plain-English summary line up top; jargon is hover-explainable via <Term>.
 *
 * Polls /options/baskets every 15s. Self-contained; embed on the Options hub
 * Overview and (compact) on the Dashboard.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Card, CardContent, Typography, Chip, CircularProgress, Button, Tooltip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { optionsService } from '../../services/options.service';
import { optionStrategyService } from '../../services/optionStrategy.service';
import { computeInstanceStats } from './OptionInstancePerformance';
import { cycleStats } from './cycleFormat';
import { OptionMetric, PlainStatus, Term, RiskMeter, useOptionColors, useAccountCapital, inr0, inrSigned, timeToGo } from './shared/OptionsUi';

interface Basket {
  id: number; strategy: string; status: string; tradingMode?: 'PAPER' | 'LIVE'; campaignId?: number | null;
  maxLoss?: number | null; maxProfit?: number | null; closedAt?: string | null;
  realizedPnl?: number | null; adjustmentNeeded?: boolean;
  adjustmentInfo?: { severity?: string } | null;
  live?: { unrealizedPnl: number; nearExpiryMs: number | null } | null;
}

const isOpen = (b: Basket) => b.status === 'EXECUTED' && !b.closedAt;
const istDay = (t: number) => new Date(t).toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

const OptionsPortfolioHeader: React.FC<{ compact?: boolean; onViewAll?: () => void; hideWhenEmpty?: boolean; liveOnly?: boolean }> = ({ compact, onViewAll, hideWhenEmpty, liveOnly }) => {
  const c = useOptionColors();
  const capital = useAccountCapital();
  const [baskets, setBaskets] = useState<Basket[]>([]);
  // Auto-engine (DeltaMiner/Rudra/ThetaMiner/…) roll-up folded into the same glance.
  const [engines, setEngines] = useState<{ running: number; total: number; open: number; pnl: number; hasPnl: boolean; risk: number; riskPaper: number; openPnl: number }>({ running: 0, total: 0, open: 0, pnl: 0, hasPnl: false, risk: 0, riskPaper: 0, openPnl: 0 });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r: any = await optionsService.getBaskets(undefined as any);
      const rows: Basket[] = (r?.data?.data || r?.data || []) as Basket[];
      setBaskets(Array.isArray(rows) ? rows : []);
    } catch { /* keep last */ } finally { setLoading(false); }
  }, []);

  // Auto-engine roll-up. MULTI-LEG engines carry P&L on their cycles (cycleStats) — the
  // same source the Option-AT page + AutoEngineSummary use, so all three agree (audit B2).
  // Only the legacy SINGLE-LEG engines still need the decision log for P&L.
  const loadEngines = useCallback(async () => {
    try {
      const r: any = await optionStrategyService.list();
      const insts: any[] = r.data?.data || [];
      if (!insts.length) { setEngines({ running: 0, total: 0, open: 0, pnl: 0, hasPnl: false, risk: 0, riskPaper: 0, openPnl: 0 }); return; }
      // Fetch the decision log ONLY for single-leg engines (multi-leg needs none).
      const logs = await Promise.all(insts.map(i => i.multiLeg
        ? Promise.resolve([])
        : optionStrategyService.log(i.id).then((lr: any) => lr.data?.data || []).catch(() => [])));
      let open = 0, pnl = 0, risk = 0, riskPaper = 0, openPnl = 0; let hasPnl = false;
      insts.forEach((inst, i) => {
        // On the dashboard (liveOnly) only LIVE activity counts, so mode-filter the source.
        if (inst.multiLeg) {
          const cycles = liveOnly ? (inst.cycles || []).filter((cy: any) => cy.mode === 'LIVE') : (inst.cycles || []);
          const st = cycleStats(cycles);
          open += st.openCount;
          openPnl += st.open;
          if (cycles.length) { pnl += st.total; hasPnl = true; }
          // Defined risk of open cycles = the deployed margin (audit B3).
          // A-024: real money and paper are kept apart (paper is never "% of your capital").
          for (const cy of cycles.filter((cy: any) => cy.status === 'OPEN')) {
            if (cy.mode === 'PAPER') riskPaper += Number(cy.investedInr) || 0; else risk += Number(cy.investedInr) || 0;
          }
        } else {
          const positions = liveOnly ? (inst.positions || []).filter((p: any) => p.mode === 'LIVE') : (inst.positions || []);
          const events = liveOnly ? (logs[i] || []).filter((e: any) => e.mode === 'LIVE') : logs[i];
          const st = computeInstanceStats(positions, events);
          open += st.openCount;
          if (st.openCount > 0 && Number.isFinite(st.open)) openPnl += Number(st.open);
          const hasActivity = !liveOnly || (events || []).length > 0 || positions.some((p: any) => p.status && p.status !== 'FLAT');
          if (hasActivity && Number.isFinite(st.total)) { pnl += Number(st.total); hasPnl = true; }
        }
      });
      const running = insts.filter(i => i.schedules?.[0]?.enabled).length;
      setEngines({ running, total: insts.length, open, pnl, hasPnl, risk, riskPaper, openPnl });
    } catch { /* keep last */ }
  }, [liveOnly]);

  useEffect(() => { load(); const id = setInterval(load, 15_000); return () => clearInterval(id); }, [load]);
  useEffect(() => { loadEngines(); const id = setInterval(loadEngines, 30_000); return () => clearInterval(id); }, [loadEngines]);

  const roll = useMemo(() => {
    // Exclude campaign-tranche baskets (they're rolled up in the campaign cards, so counting them
    // here double-counts / shows P&L when "Placed baskets → Open" reads 0). On the dashboard
    // (liveOnly) also exclude PAPER baskets — reviewed on the Options/Trades pages.
    const visible = (liveOnly ? baskets.filter(b => b.tradingMode !== 'PAPER') : baskets).filter(b => !b.campaignId);
    const open = visible.filter(isOpen);
    const hasLive = open.some(b => b.live);
    const livePnl = open.reduce((s, b) => s + (b.live?.unrealizedPnl || 0) + (b.live ? Number((b as any).adjustmentsRealizedPnl || 0) : 0), 0);   // open baskets' total: open legs + adjustment bookings
    // A fixed-₹ basket's planned risk is its stop (total-P&L based), not the structure's
    // theoretical max loss (unbounded / grid-edge for a naked strangle).
    // FIXED: risk = the stop's loss; a profit-floor stop (negative slAmountInr) risks ₹0 at the stop.
    const riskOf = (b: Basket) => ((b as any).exitMode === 'FIXED' && Number((b as any).slAmountInr) !== 0 && Number.isFinite(Number((b as any).slAmountInr))
      ? Math.max(0, Number((b as any).slAmountInr)) : Math.abs(Number(b.maxLoss || 0)));
    const openRisk = open.filter(b => b.tradingMode !== 'PAPER').reduce((s, b) => s + riskOf(b), 0);
    const openRiskPaper = open.filter(b => b.tradingMode === 'PAPER').reduce((s, b) => s + riskOf(b), 0);
    const nearestMs = open.reduce<number | null>((m, b) => {
      const e = b.live?.nearExpiryMs; if (!e) return m; return m == null ? e : Math.min(m, e);
    }, null);
    const attention = open.filter(b => b.adjustmentNeeded);
    const urgent = attention.some(b => b.adjustmentInfo?.severity === 'urgent');
    const paper = open.filter(b => b.tradingMode === 'PAPER').length;
    const today = istDay(Date.now());
    const todayRealized = visible
      .filter(b => b.closedAt && istDay(new Date(b.closedAt).getTime()) === today && b.realizedPnl != null)
      .reduce((s, b) => s + Number(b.realizedPnl), 0);
    const allPaper = open.length > 0 && paper === open.length;
    return { open, hasLive, livePnl, openRisk, openRiskPaper, nearestMs, attention, urgent, paper, allPaper, todayRealized };
  }, [baskets, liveOnly]);

  const nOpen = roll.open.length;
  const ttg = timeToGo(roll.nearestMs);

  // On the dashboard we only want this to appear when there's something to show.
  if (hideWhenEmpty && nOpen === 0 && roll.todayRealized === 0 && engines.open === 0) return null;

  // Headline scope = EVERYTHING the Positions count covers: open baskets + auto-engine cycles
  // (audit B4). Engine P&L is cycle-based (B2) and only contributes when there's activity, so an
  // idle engine can't drag the glance. The auto-engines line below still shows the split.
  const totalPositions = nOpen + engines.open;
  // The headline is "Live P&L — unrealised, open positions": only OPEN P&L belongs in it.
  // (It used to add the engines' all-time total, so a closed engine's realised loss turned a
  // winning open basket into "your 1 open position is down".) The engines line keeps the total.
  const combinedPnl = (roll.hasLive ? roll.livePnl : 0) + (engines.open > 0 ? engines.openPnl : 0);
  const hasAnyPnl = roll.hasLive || engines.open > 0;
  // A-024: only REAL-money risk is compared with your capital; paper risk is shown beside it.
  const totalRisk = roll.openRisk + engines.risk;                                // LIVE baskets + LIVE engine margin (B3)
  const paperRisk = roll.openRiskPaper + engines.riskPaper;
  const riskPctOfCapital = capital && capital > 0 && totalRisk > 0 ? (totalRisk / capital) * 100 : null;
  const allPaper = totalRisk === 0 && paperRisk > 0;
  const riskSub = allPaper ? 'paper only — not real money'
    : riskPctOfCapital != null ? `${riskPctOfCapital.toFixed(1)}% of your capital${paperRisk > 0 ? ` · +${inr0(paperRisk)} paper` : ''}`
    : paperRisk > 0 ? `worst-case · +${inr0(paperRisk)} paper` : 'worst-case across open';

  // Plain-English one-liner — about ALL your open option positions (baskets + auto-engines).
  const summary: { tone: 'good' | 'bad' | 'warn' | 'neutral'; emoji: string; text: React.ReactNode } = (() => {
    if (totalPositions === 0) return { tone: 'neutral', emoji: '📭', text: 'No open option positions right now. Build one from your outlook to get started.' };
    if (roll.attention.length) return {
      tone: roll.urgent ? 'bad' : 'warn', emoji: roll.urgent ? '🚨' : '⚠️',
      text: <>{roll.attention.length} of your {totalPositions} position{totalPositions === 1 ? '' : 's'} {roll.attention.length === 1 ? 'needs' : 'need'} attention — the price has moved against the plan. Review below.</>,
    };
    if (hasAnyPnl && combinedPnl >= 0) return { tone: 'good', emoji: '✅', text: <>Your {totalPositions} open position{totalPositions === 1 ? '' : 's'} {totalPositions === 1 ? 'is' : 'are'} on track — up {inrSigned(combinedPnl)} right now.</> };
    if (hasAnyPnl) return { tone: 'warn', emoji: '📉', text: <>Your {totalPositions} open position{totalPositions === 1 ? '' : 's'} {totalPositions === 1 ? 'is' : 'are'} down {inrSigned(combinedPnl)} right now — within plan unless flagged.</> };
    return { tone: 'neutral', emoji: '⏳', text: <>{totalPositions} open position{totalPositions === 1 ? '' : 's'} — waiting for a live mark.</> };
  })();

  return (
    <Card variant="outlined" sx={{ borderLeft: `4px solid ${c.accent}` }}>
      <CardContent sx={{ pb: compact ? 1.5 : 2, '&:last-child': { pb: compact ? 1.5 : 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontWeight: 800, fontSize: compact ? '0.95rem' : '1.05rem' }}>Your options at a glance</Typography>
          {roll.paper > 0 && <Chip size="small" label={`${roll.paper} paper`} color="warning" sx={{ height: 18, fontSize: '0.58rem', fontWeight: 700 }} />}
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {loading && <CircularProgress size={14} />}
            <Tooltip arrow title="Refresh"><Button size="small" variant="text" onClick={load} sx={{ minWidth: 0, p: 0.5 }}><RefreshIcon fontSize="small" /></Button></Tooltip>
            {onViewAll && nOpen > 0 && <Button size="small" variant="text" onClick={onViewAll}>View all</Button>}
          </Box>
        </Box>

        <PlainStatus tone={summary.tone} emoji={summary.emoji}>{summary.text}</PlainStatus>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', sm: 'repeat(4,1fr)', md: 'repeat(5,1fr)' }, gap: 1.5, mt: 1.5 }}>
          <OptionMetric term="netToClose" label={roll.allPaper && engines.open === 0 ? 'Paper P&L' : 'Live P&L'} value={hasAnyPnl ? inrSigned(combinedPnl) : '—'} color={hasAnyPnl ? (combinedPnl >= 0 ? c.profit : c.loss) : undefined} sub={roll.allPaper && engines.open === 0 ? 'unrealised, paper positions' : roll.paper > 0 ? 'unrealised, open (incl. paper)' : 'unrealised, open positions'} />
          <OptionMetric term="maxLoss" label={allPaper ? 'At risk (paper)' : 'At risk'} value={inr0(allPaper ? paperRisk : totalRisk)} color={allPaper ? c.warn : c.loss} sub={riskSub} />
          <OptionMetric label="Positions" value={`${totalPositions}`} sub={`${nOpen} basket${nOpen === 1 ? '' : 's'} · ${engines.open} auto`} />
          <OptionMetric term="expiry" label="Next expiry" value={ttg ? ttg.label : '—'} color={ttg?.soon ? c.warn : undefined} sub={ttg?.soon ? 'settling soon' : 'nearest basket leg'} />
          <OptionMetric label="Booked today" value={roll.todayRealized !== 0 ? inrSigned(roll.todayRealized) : '₹0'} color={roll.todayRealized > 0 ? c.profit : roll.todayRealized < 0 ? c.loss : undefined} sub="closed baskets, today" />
        </Box>

        {/* Auto-trade engines — folded into the same glance. On the dashboard (liveOnly) only
            show when there's LIVE activity (paper engine positions are filtered out above). */}
        {engines.total > 0 && (!liveOnly || engines.open > 0 || engines.hasPnl) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.25, flexWrap: 'wrap', px: 0.25 }}>
            <Chip size="small" label="Auto-engines" sx={{ height: 18, fontSize: '0.58rem', fontWeight: 800 }} />
            <Typography variant="caption" color="text.secondary">
              {engines.running}/{engines.total} running · {engines.open} open
              {engines.hasPnl && <> · P&amp;L <Box component="span" sx={{ fontWeight: 800, color: engines.pnl >= 0 ? c.profit : c.loss }}>{inrSigned(engines.pnl)}</Box></>}
            </Typography>
          </Box>
        )}

        {/* Risk vs capital meter — a clear "how big is this bet overall" (baskets + engines). */}
        {totalRisk > 0 && (
          <Box sx={{ mt: 1.25, maxWidth: 360 }}>
            <RiskMeter riskInr={totalRisk} capitalInr={capital ?? undefined} compact />
          </Box>
        )}
        {allPaper && (
          <Typography variant="caption" data-testid="options-paper-only" sx={{ display: 'block', mt: 1, color: c.warn }}>
            Paper positions only — no real money is at risk. The risk meter appears when a LIVE position is open.
          </Typography>
        )}

        {!compact && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.2 }}>
            <Term k="netToClose">Live P&amp;L</Term> is what you’d gain/lose if you closed everything now. <Term k="maxLoss">At&nbsp;risk</Term> is the most these positions can lose if held to expiry. Positions are auto-managed with take-profit / stop-loss.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default OptionsPortfolioHeader;
